import { DIALOG_THEME_MAPPING } from 'schema/src/kinds/dialog/theme';
import { POLL_THEME_MAPPING } from 'schema/src/kinds/poll/theme';
import { WEBVIEW_THEME_MAPPING } from 'schema/src/kinds/webview/theme';
import { ThemeVariableValue } from 'schema/src/themes';
import { TrueTheme } from 'schema/src/types/poll.generated';
import {
  ROOT_TDS_DARK_THEME_SELECTOR,
  ROOT_TDS_LIGHT_THEME_SELECTOR,
  ROOT_TUI_DARK_THEME_SELECTOR,
  ROOT_TUI_LIGHT_THEME_SELECTOR,
} from '../constants';
import { createStyleContent, getPollTemplateForTheme } from '../themeUtils';

type ThemeColorMapping =
  | typeof POLL_THEME_MAPPING.colors
  | typeof DIALOG_THEME_MAPPING.colors
  | typeof WEBVIEW_THEME_MAPPING.colors;

const getRootStyleSelectors = (
  template: ReturnType<typeof getPollTemplateForTheme>,
): { light: string; dark: string } => {
  if (template === 'poll') {
    return { light: ROOT_TUI_LIGHT_THEME_SELECTOR, dark: ROOT_TUI_DARK_THEME_SELECTOR };
  }

  return { light: ROOT_TDS_LIGHT_THEME_SELECTOR, dark: ROOT_TDS_DARK_THEME_SELECTOR };
};

export const getColorsStyleContent = (
  colors:
    | TrueTheme['data']['dialog']['colors']
    | TrueTheme['data']['poll']['colors']
    | TrueTheme['data']['webview']['colors'],
  colorMapping: ThemeColorMapping,
  template: ReturnType<typeof getPollTemplateForTheme>,
) => {
  if (Object.keys(colors).length === 0) {
    return '';
  }

  const lightThemeStyles: string[] = [];
  const darkThemeStyles: string[] = [];

  for (const [key, value] of Object.entries(colors)) {
    const variables = colorMapping[key as keyof typeof colorMapping];

    if (variables) {
      const styles = appendColorThemeStyles(variables, value);
      lightThemeStyles.push(...styles.light);
      darkThemeStyles.push(...styles.dark);
    }
  }

  const selector = getRootStyleSelectors(template);
  return `${createStyleContent(lightThemeStyles, selector.light)} ${createStyleContent(darkThemeStyles, selector.dark)}`;
};

const appendColorThemeStyles = (
  cssVarNames: ThemeVariableValue,
  value: { dark: string; light: string },
): { light: string[]; dark: string[] } => {
  const lightStyles: string[] = [];
  const darkStyles: string[] = [];

  const cssVarNamesToArray = Array.isArray(cssVarNames) ? cssVarNames : [cssVarNames];
  cssVarNamesToArray.forEach(cssVarName => {
    lightStyles.push(`${cssVarName}: ${value.light};`);
    darkStyles.push(`${cssVarName}: ${value.dark};`);
  });

  return { light: lightStyles, dark: darkStyles };
};
